import { AppHelmet } from '@pendwa/common-browser/components/AppHelmet/index.js';
import { ChooseDisplayLanguageDuo } from '@pendwa/common-browser/components/DisplayLanguage/ChooseDisplayLanguageDuo/index.js';
import { ErrorBanner } from '@pendwa/common-browser/components/ErrorBanner/index.js';
import { LayoutContainer } from '@pendwa/common-browser/components/LayoutContainer/index.js';
import { LayoutContainerNarrow } from '@pendwa/common-browser/components/LayoutContainerNarrow/index.js';
import { LayoutPageSection } from '@pendwa/common-browser/components/LayoutPageSection/index.js';
import { LayoutSpacer } from '@pendwa/common-browser/components/LayoutSpacer/index.js';
import { LoadingPage } from '@pendwa/common-browser/components/Loading/LoadingPage/index.js';
import { Footer } from '@pendwa/common-browser/components/Navs/Footer/index.js';
import { Nav } from '@pendwa/common-browser/components/Navs/Nav/index.js';
import { ContextPageData, EnumDisplayLanguage } from '@pendwa/common-browser/context/PageData/index.js';
import { extractGraphQLErrorMessage } from '@pendwa/common-browser/core/apollo/utils.js';
import { redirectToPath } from '@pendwa/common-browser/core/appLocation/index.js';
import { getAbsoluteUrlToSite } from '@pendwa/common-browser/utils/getAbsoluteUrlToSite.js';
import { rootRoutes } from '@pendwa/common/core/rootRoutes/index.js';
import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { ApplyCreditVoucher } from '../../../../components/UserComponents/ApplyCreditVoucher/index.js';
import { getFooterConfig } from '../../../../core/footer/config/index.js';
import { getNavConfig } from '../../../../core/nav/config/index.js';

export function Screen() {
  const location = useLocation();
  const history = useHistory();

  const {
    loading: loading_page_data,
    viewer,
    display_language,
    currency,
  } = React.useContext(ContextPageData);

  const urlSearchParams = new URLSearchParams(location.search);
  const raw_number_of_credits_needed = urlSearchParams.get('credits');
  const next = urlSearchParams.get('next') ?? undefined;
  const number_of_credits_needed =
    raw_number_of_credits_needed && !Number.isNaN(Number(raw_number_of_credits_needed))
      ? Number(Number.parseFloat(raw_number_of_credits_needed).toFixed(2))
      : undefined;

  const handleSuccess = () => {
    if (next) {
      redirectToPath(next, false);
    } else {
      redirectToPath(rootRoutes.app.status.voucherApplied.main.get(), false);
    }
  };

  let $content: React.ReactNode = null;

  if (loading_page_data || currency?.loading) {
    $content = <LoadingPage />;
  } else if (!viewer) {
    $content = (
      <div className="flex justify-center text-center">
        <button
          type="button"
          className="inline-flex items-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:text-sm"
          onClick={() => history.push(rootRoutes.app.account.login.get(window.location.href))}
        >
          {display_language === EnumDisplayLanguage.swahili
            ? 'Ingia uongeze vocha'
            : 'Sign In to Apply Voucher'}
        </button>
      </div>
    );
  } else if (currency?.error) {
    $content = (
      <ErrorBanner
        message={`We were not able to get the current exchange rates. ${extractGraphQLErrorMessage(currency?.error)}`}
        actionButtons={[
          {
            text: 'Retry',
            onClick: () => currency?.refetch(),
          },
        ]}
      />
    );
  } else if (!currency?.data?.usd_tzs) {
    $content = (
      <ErrorBanner
        message={'We were not able to get the current exchange rates.'}
        actionButtons={[
          {
            text: 'Retry',
            onClick: () => currency?.refetch(),
          },
        ]}
      />
    );
  } else {
    $content = (
      <LayoutSpacer>
        <p className="text-center text-base text-gray-600">
          {display_language === EnumDisplayLanguage.swahili
            ? 'Ingiza namba au herufi za vocha yako kwenye sanduku hapo chini kuongeza salio. Vocha itakuongezea salio na utaweza kutumia salio lako kununua au kukodisha filamu Pendwa.'
            : 'Enter voucher code below to add credits. You can use credits to buy or rent movies on Pendwa.'}
        </p>

        <ApplyCreditVoucher
          number_of_credits_needed={number_of_credits_needed}
          handleSuccess={() => {
            handleSuccess();
          }}
          usd_tzs={currency?.data.usd_tzs}
        />
      </LayoutSpacer>
    );
  }

  return (
    <React.Fragment>
      <AppHelmet
        title={`Apply A Credit Voucher | ${browserRootConfig.project.name}`}
        description={`Apply a credit voucher to add credits into your account.`}
        canonical_url={getAbsoluteUrlToSite(rootRoutes.app.voucher.main.get())}
      />

      <Nav getConfig={getNavConfig} />

      <LayoutPageSection className="flex justify-center bg-gray-50">
        <LayoutContainerNarrow>
          <LayoutSpacer>
            <div className="flex justify-center">
              <ChooseDisplayLanguageDuo />
            </div>

            <img
              className="mx-auto h-24 w-auto"
              src={`https://res.cloudinary.com/pendwa/image/upload/w_512,h_512/public/assets/${DOMAIN_ENV}/logo/transparent-2560×2560.png`}
              alt={`${browserRootConfig.project.name} logo`}
            />

            <h1 className="text-center text-3xl font-extrabold text-gray-900">
              {display_language === EnumDisplayLanguage.swahili
                ? 'Tumia Vocha Kuongeza Salio (Credits)'
                : 'Apply Credit Voucher'}
            </h1>

            {$content}
          </LayoutSpacer>
        </LayoutContainerNarrow>
      </LayoutPageSection>

      <LayoutPageSection>
        <LayoutContainer>
          <Footer getConfig={getFooterConfig} />
        </LayoutContainer>
      </LayoutPageSection>
    </React.Fragment>
  );
}
